import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import axios from 'axios';
import i18n from '../i18n';
import { supabase } from '@/lib/supabase';
import { Capacitor } from '@capacitor/core';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mapServerNotification = (notification) => {
  const type = notification?.type;
  if (type === 'trial_ending') {
    return {
      id: notification.notification_id,
      type,
      icon: 'solar:wallet-money-linear',
      color: 'amber',
      title: notification.title,
      message: notification.message,
      action: 'subscription'
    };
  }

  if (type === 'ticket_update') {
    return {
      id: notification.notification_id,
      type,
      icon: 'solar:chat-round-dots-linear',
      color: 'blue',
      title: notification.title,
      message: notification.message,
      action: 'support'
    };
  }

  return {
    id: notification.notification_id,
    type,
    icon: 'solar:bell-linear',
    color: 'stone',
    title: notification.title,
    message: notification.message,
    action: 'suggestion'
  };
};

const NotificationBell = ({ userId, onNotificationClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPushModal, setShowPushModal] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [user, setUser] = useState(null);

  // Persist read/dismissed notification IDs in localStorage
  const storageKey = `bh_read_notifs_${userId}`;
  const getReadIds = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch { return []; }
  }, [storageKey]);
  const [readIds, setReadIds] = useState(getReadIds);

  // Notification history (persisted in localStorage)
  const historyKey = `bh_notif_history_${userId}`;
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`bh_notif_history_${userId}`) || '[]'); }
    catch { return []; }
  });

  const saveToHistory = useCallback((notifs) => {
    setHistory(prev => {
      const map = new Map(prev.map(n => [n.id, n]));
      notifs.forEach(n => {
        if (!map.has(n.id)) map.set(n.id, { ...n, timestamp: new Date().toISOString(), status: 'active' });
      });
      const activeIds = new Set(notifs.map(n => n.id));
      map.forEach((v, k) => { if (v.status === 'active' && !activeIds.has(k)) map.set(k, { ...v, status: 'done' }); });
      const arr = Array.from(map.values()).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 100);
      localStorage.setItem(historyKey, JSON.stringify(arr));
      return arr;
    });
  }, [historyKey]);

  const markHistoryRead = useCallback((id) => {
    setHistory(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, status: 'read' } : n);
      localStorage.setItem(historyKey, JSON.stringify(updated));
      return updated;
    });
  }, [historyKey]);

  const markAsRead = useCallback((id) => {
    setReadIds(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }, [storageKey]);

  const checkNotifications = useCallback(async () => {
    if (!userId) return;
    
    const newNotifications = [];
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    
    try {
      // Fetch user for reminder time
      const userResponse = await axios.get(`${API}/user/${userId}`);
      const userData = userResponse.data;
      setUser(userData);

      try {
        const serverNotificationsResponse = await axios.get(`${API}/notifications/${userId}`);
        const serverNotifications = Array.isArray(serverNotificationsResponse.data)
          ? serverNotificationsResponse.data.map(mapServerNotification)
          : [];
        newNotifications.push(...serverNotifications);
      } catch (serverNotificationError) {
        console.debug('Failed to fetch server notifications', serverNotificationError?.message || serverNotificationError);
      }
      
      // Parse reminder time (format: "HH:MM")
      const reminderTime = userData.reminder_time || "19:00";
      const [reminderHour, reminderMinute] = reminderTime.split(':').map(Number);
      
      // Check if it's time for daily reminder (within 30 min window)
      const reminderStart = reminderHour * 60 + reminderMinute;
      const currentTime = currentHour * 60 + currentMinutes;
      
      if (currentTime >= reminderStart && currentTime <= reminderStart + 30) {
        // Check if suggestion is pending
        const suggestionResponse = await axios.get(`${API}/suggestion/today/${userId}`);
        if (suggestionResponse.data && suggestionResponse.data.status === 'pending') {
          // pick rotating phrase for reminder
          const hourForPhrase = reminderHour;
          const ns = 'notifications.phrases';
          const pickArray = (key) => i18n.t(`${ns}.${key}`, { returnObjects: true }) || [];
          const momentKey = hourForPhrase < 12 ? 'morning' : (hourForPhrase >= 18 ? 'evening' : 'neutral');
          const pool = (pickArray(momentKey).length ? pickArray(momentKey) : pickArray('neutral'))
            .concat(pickArray('identity'))
            .concat(pickArray('minimal'));
          const message = pool[Math.floor(Math.random() * pool.length)];

          if (window.posthog && typeof window.posthog.capture === 'function') {
            window.posthog.capture('notification_phrase_shown', {
              source: 'in_app_bell',
              phrase: message,
              moment: momentKey,
              lang: i18n.language || null,
              user_id: userId || null
            });
          }

          newNotifications.push({
            id: 'daily_reminder',
            type: 'reminder',
            icon: 'solar:stars-linear',
            color: 'amber',
            title: t('notifications.dailyReminder'),
            message: message || t('notifications.dailyReminderMsg'),
            action: 'suggestion'
          });
        }
      }
      
      // Check if it's evening check-in time based on user's preference
      const checkinTime = userData.checkin_time || "22:00";
      const [checkinHour, checkinMinute] = checkinTime.split(':').map(Number);
      const checkinStart = checkinHour * 60 + checkinMinute;
      
      // Show notification from 18:00 (or 3 hours before checkin time) until midnight
      const eveningStart = Math.min(18 * 60, checkinStart - 180); // 18:00 o 3h prima del checkin
      const midnight = 24 * 60;
      if (currentTime >= eveningStart && currentTime < midnight) {
        const checkinResponse = await axios.get(`${API}/checkin/today/${userId}`);
        // Only show notification if check-in has NOT been completed today
        if (!checkinResponse.data || !checkinResponse.data.checkin_id) {
          const hourForPhrase = checkinHour;
          const ns = 'notifications.phrases';
          const pickArray = (key) => i18n.t(`${ns}.${key}`, { returnObjects: true }) || [];
          const pool = (pickArray('evening').length ? pickArray('evening') : pickArray('neutral'))
            .concat(pickArray('identity'))
            .concat(pickArray('minimal'));
          const message = pool[Math.floor(Math.random() * pool.length)];

          if (window.posthog && typeof window.posthog.capture === 'function') {
            window.posthog.capture('notification_phrase_shown', {
              source: 'in_app_bell',
              phrase: message,
              moment: 'evening',
              lang: i18n.language || null,
              user_id: userId || null
            });
          }

          newNotifications.push({
            id: `evening_checkin_${now.toISOString().slice(0, 10)}`,
            type: 'checkin',
            icon: 'solar:moon-linear',
            color: 'indigo',
            title: t('notifications.eveningCheckin'),
            message: message || t('notifications.eveningCheckinMsg'),
            action: 'checkin'
          });
        }
      }
      
      // Check for progressive (quick) question and surface as a notification
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const authToken = session?.access_token;
        if (authToken) {
          const progRes = await axios.get(`${API}/profile/progressive/next`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          if (progRes.data && progRes.data.key) {
            newNotifications.push({
              id: `progressive_${progRes.data.key}`,
              type: 'progressive',
              icon: 'solar:stars-linear',
              color: 'blue',
              // use server title as message; fallback to a short label
              title: progRes.data.title || t('notifications.quickQuestion') || 'Quick question',
              message: progRes.data.title || '',
              action: 'progressive'
            });
          }
        }
      } catch (e) {
        // non-fatal; ignore 401/400 responses silently
        if (e.response && e.response.status !== 401 && e.response.status !== 400) {
          console.debug('No progressive question or failed to fetch it', e?.message || e);
        }
      }

      // Check for upcoming events
      const eventsResponse = await axios.get(`${API}/events/upcoming/${userId}?days=15`);
      const events = eventsResponse.data || [];
      
      events.forEach(event => {
        const daysUntil = event.days_until;
        
        // Notify for events: today, tomorrow, 7 days, 15 days
        if (daysUntil === 0) {
          newNotifications.push({
            id: `event_${event.event_id}_today`,
            type: 'event',
            icon: 'solar:calendar-linear',
            color: 'red',
            title: t('notifications.eventToday'),
            message: `${event.title}`,
            action: 'events'
          });
        } else if (daysUntil === 1) {
          newNotifications.push({
            id: `event_${event.event_id}_tomorrow`,
            type: 'event',
            icon: 'solar:calendar-linear',
            color: 'orange',
            title: t('notifications.eventTomorrow'),
            message: `${event.title}`,
            action: 'events'
          });
        } else if (daysUntil === 7) {
          newNotifications.push({
            id: `event_${event.event_id}_week`,
            type: 'event',
            icon: 'solar:calendar-linear',
            color: 'blue',
            title: t('notifications.eventWeek'),
            message: `${event.title}`,
            action: 'events'
          });
        } else if (daysUntil === 15) {
          newNotifications.push({
            id: `event_${event.event_id}_15days`,
            type: 'event',
            icon: 'solar:calendar-linear',
            color: 'stone',
            title: t('notifications.event15Days'),
            message: `${event.title}`,
            action: 'events'
          });
        }
      });

      // Achievements are now celebrated via modal — no longer pushed to notification bell

      // Check if push notifications are NOT enabled — nudge every 7 days
      try {
        const nudgeKey = `bh_push_nudge_${userId}`;
        const lastNudge = localStorage.getItem(nudgeKey);
        const daysSinceNudge = lastNudge ? (Date.now() - Number(lastNudge)) / (1000 * 60 * 60 * 24) : Infinity;

        let pushGranted = false;
        const platform = Capacitor.getPlatform();
        if (platform === 'ios' || platform === 'android') {
          try {
            const { PushNotifications } = await import('@capacitor/push-notifications');
            const permStatus = await PushNotifications.checkPermissions();
            pushGranted = permStatus.receive === 'granted';
          } catch { /* plugin not available */ }
        } else if ('Notification' in window) {
          pushGranted = Notification.permission === 'granted';
        }

        if (!pushGranted && daysSinceNudge >= 7) {
          localStorage.setItem(nudgeKey, String(Date.now()));
          newNotifications.push({
            id: 'enable_push_nudge',
            type: 'push_nudge',
            icon: 'solar:bell-bing-linear',
            color: 'green',
            title: t('notifications.pushNudgeTitle'),
            message: t('notifications.pushNudgeMsg'),
            action: 'enable_notifications'
          });
        }
      } catch (e) {
        console.debug('Push nudge check failed', e?.message);
      }
      
      const dedupedNotifications = Array.from(new Map(newNotifications.map((notification) => [notification.id, notification])).values());
      // Filter out daily reminder and evening checkin — these are already sent as OS push notifications
      const filteredNotifications = dedupedNotifications.filter(n => n.type !== 'reminder' && n.type !== 'checkin');
      setNotifications(filteredNotifications.filter(n => !readIds.includes(n.id)));
      saveToHistory(dedupedNotifications);
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  }, [userId, t, readIds, saveToHistory]);

  useEffect(() => {
    checkNotifications();
    
    // Check every 5 minutes
    const interval = setInterval(checkNotifications, 5 * 60 * 1000);

    // Refresh immediately when a progressive question is answered or any action completes
    const onRefresh = () => checkNotifications();
    window.addEventListener('progressive-answered', onRefresh);
    window.addEventListener('notifications-refresh', onRefresh);

    // Admin: simulate trial expiration notification
    const onSimulateTrialExpiring = () => {
      const startDate = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
      const dateStr = i18n.language === 'it'
        ? startDate.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
        : i18n.language === 'es'
        ? startDate.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
        : startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const sim = {
        id: `sim_trial_ending_${Date.now()}`,
        type: 'trial_ending',
        icon: 'solar:wallet-money-linear',
        color: 'amber',
        title: i18n.language === 'it' ? 'Il tuo periodo di prova sta per scadere' : i18n.language === 'es' ? 'Tu período de prueba está por terminar' : 'Your free trial is ending soon',
        message: i18n.language === 'it'
          ? `Il tuo abbonamento inizierà il ${dateStr}.`
          : i18n.language === 'es'
          ? `Tu suscripción comenzará el ${dateStr}.`
          : `Your subscription will start on ${dateStr}.`,
        action: 'subscription',
      };
      setNotifications(prev => {
        const merged = [sim, ...prev];
        return Array.from(new Map(merged.map(n => [n.id, n])).values());
      });
    };

    // Admin: simulate subscription active notification
    const onSimulateSubActive = () => {
      const sim = {
        id: `sim_sub_active_${Date.now()}`,
        type: 'subscription_active',
        icon: 'solar:verified-check-bold',
        color: 'green',
        title: i18n.language === 'it' ? 'Abbonamento attivo' : i18n.language === 'es' ? 'Suscripción activa' : 'Subscription active',
        message: i18n.language === 'it' ? 'Il tuo abbonamento Premium è attivo. Grazie!' : i18n.language === 'es' ? '¡Tu suscripción Premium está activa. ¡Gracias!' : 'Your Premium subscription is active. Thank you!',
        action: 'subscription',
      };
      setNotifications(prev => {
        const merged = [sim, ...prev];
        return Array.from(new Map(merged.map(n => [n.id, n])).values());
      });
    };

    window.addEventListener('notifications-simulate-trial', onSimulateTrialExpiring);
    window.addEventListener('notifications-simulate-sub', onSimulateSubActive);

    return () => {
      clearInterval(interval);
      window.removeEventListener('progressive-answered', onRefresh);
      window.removeEventListener('notifications-refresh', onRefresh);
      window.removeEventListener('notifications-simulate-trial', onSimulateTrialExpiring);
      window.removeEventListener('notifications-simulate-sub', onSimulateSubActive);
    };
  }, [checkNotifications, t]);

  const handleNotificationClick = (notification) => {
    // Keep notification visible until action is completed (next poll removes it naturally)
    markHistoryRead(notification.id);
    setShowDropdown(false);

    // Show detail modal for trial/subscription notifications
    if (notification.type === 'trial_ending' || notification.type === 'subscription_active') {
      setDetailModal(notification);
      return;
    }

    // If the parent page provided a handler, use it (Dashboard handles modals directly)
    if (notification.action === 'enable_notifications') {
      setShowPushModal(true);
      return;
    }

    if (onNotificationClick) {
      onNotificationClick(notification.action);
      // For dashboard-only actions (progressive, checkin), the parent handler
      // may not handle them (only Dashboard does). If we're not on /dashboard,
      // fall through to navigate so the action still works.
      const isDashboard = window.location.pathname === '/dashboard' || window.location.pathname === '/';
      if (isDashboard) return;
      const dashboardOnlyActions = ['progressive', 'checkin'];
      if (!dashboardOnlyActions.includes(notification.action)) return;
    }

    // Fallback: navigate to the correct page so notifications work from ANY page
    switch (notification.action) {
      case 'checkin':
        navigate('/dashboard?openCheckin=1');
        break;
      case 'suggestion':
        navigate('/dashboard');
        break;
      case 'events':
        navigate('/events');
        break;
      case 'subscription':
        navigate('/subscription');
        break;
      case 'support':
        navigate('/support');
        break;
      case 'achievements':
        navigate('/achievements');
        break;
      case 'progressive':
        navigate('/dashboard?showProgressive=1');
        break;
      default:
        navigate('/dashboard');
        break;
    }
  };

  const dismissNotification = (e, notificationId) => {
    e.stopPropagation();
    markAsRead(notificationId);
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const dismissAll = () => {
    notifications.forEach(n => markAsRead(n.id));
    setNotifications([]);
    // Mark all as read on backend so they don't reappear on reload
    if (userId) {
      axios.post(`${API}/notifications/${userId}/read-all`).catch(err =>
        console.error('[NotificationBell] Failed to mark all read on backend:', err)
      );
    }
  };

  const getIconStyle = (color) => {
    const colors = {
      amber:  { background: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
      indigo: { background: 'rgba(99,102,241,0.15)', color: '#818cf8' },
      red:    { background: 'rgba(239,68,68,0.15)',   color: '#f87171' },
      orange: { background: 'rgba(249,115,22,0.15)',  color: '#fb923c' },
      blue:   { background: 'rgba(59,130,246,0.15)',   color: '#60a5fa' },
      green:  { background: 'rgba(34,197,94,0.15)',    color: '#4ade80' },
      stone:  { background: 'var(--c-surface-card)',  color: '#a8a29e' },
    };
    return colors[color] || colors.stone;
  };

  const unreadCount = notifications.length;

  const dragControls = useDragControls();

  const handleEnablePush = async () => {
    try {
      const platform = Capacitor.getPlatform();
      if (platform === 'ios' || platform === 'android') {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const result = await PushNotifications.requestPermissions();
        if (result.receive === 'granted') {
          await PushNotifications.register();
          // Remove the nudge from active notifications
          markAsRead('enable_push_nudge');
          setNotifications(prev => prev.filter(n => n.id !== 'enable_push_nudge'));
        }
      } else if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          markAsRead('enable_push_nudge');
          setNotifications(prev => prev.filter(n => n.id !== 'enable_push_nudge'));
        }
      }
    } catch (e) {
      console.debug('Error requesting push permission:', e?.message);
    }
    setShowPushModal(false);
  };

  return (
    <div className="relative">
      {/* Enable Notifications Modal */}
      {showPushModal && createPortal(
        <AnimatePresence>
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[300]"
              style={{ background: 'rgba(0,0,0,0.7)' }}
              onClick={() => setShowPushModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-[301] flex items-center justify-center p-6"
              onClick={() => setShowPushModal(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm rounded-2xl overflow-hidden"
                style={{ background: 'var(--c-surface)', border: '1px solid var(--c-border)' }}
              >
                <div className="p-6 text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(34,197,94,0.15)' }}>
                    <Icon icon="solar:bell-bing-linear" width={32} height={32} style={{ color: '#4ade80' }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--c-texPri)' }}>
                    {t('notifications.pushModalTitle', 'Stay on track')}
                  </h3>
                  <p className="text-sm mb-1" style={{ color: 'var(--c-texSec)' }}>
                    {t('notifications.pushModalBody', 'Small gestures are easy to forget. A gentle nudge at the right moment helps you show up — even on busy days.')}
                  </p>
                  <p className="text-xs mb-5" style={{ color: 'var(--c-texTer)' }}>
                    {t('notifications.pushModalSubtext', 'We only send what matters. No spam, ever.')}
                  </p>
                  <button
                    onClick={handleEnablePush}
                    className="w-full py-3 rounded-xl font-semibold text-sm transition-colors"
                    style={{ background: '#1D9E75', color: '#fff' }}
                  >
                    {t('notifications.pushModalCta', 'Enable notifications')}
                  </button>
                  <button
                    onClick={() => setShowPushModal(false)}
                    className="w-full py-2 mt-2 text-sm"
                    style={{ color: 'var(--c-texTer)', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    {t('notifications.pushModalDismiss', 'Not now')}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        </AnimatePresence>,
        document.body
      )}
      {/* Notification Detail Modal */}
      {detailModal && createPortal(
        <AnimatePresence>
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[300]"
              style={{ background: 'rgba(0,0,0,0.7)' }}
              onClick={() => setDetailModal(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-[301] flex items-center justify-center p-6"
              onClick={() => setDetailModal(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-sm rounded-2xl overflow-hidden"
                style={{ background: 'var(--c-surface)', border: '1px solid var(--c-border)' }}
              >
                <div className="p-6 text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ background: getIconStyle(detailModal.color).background }}>
                    <Icon icon={detailModal.icon} width={32} height={32}
                      style={{ color: getIconStyle(detailModal.color).color }} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--c-texPri)' }}>
                    {detailModal.title}
                  </h3>
                  <p className="text-sm mb-5" style={{ color: 'var(--c-texSec)' }}>
                    {detailModal.message}
                  </p>
                  <button
                    onClick={() => {
                      dismissNotification({ stopPropagation: () => {} }, detailModal.id);
                      setDetailModal(null);
                    }}
                    className="w-full py-3 rounded-xl font-semibold text-sm transition-colors"
                    style={{ background: getIconStyle(detailModal.color).color, color: '#000' }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        </AnimatePresence>,
        document.body
      )}

      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="w-4 h-4 bg-inherit flex items-center justify-center transition-colors relative"
      >
        <Icon icon="solar:bell-linear" width={20} height={20} style={{ color: 'var(--c-texSec)' }} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 text-white text-xs font-bold rounded-full flex items-center justify-center"
            style={{ background: '#ef4444' }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && createPortal(
        <AnimatePresence>
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200] sm:bg-transparent"
              style={{ background: 'rgba(0,0,0,0.7)' }}
              onClick={() => setShowDropdown(false)}
            />
            
            {/* Mobile: Bottom Sheet / Desktop: Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              drag="y"
              dragControls={dragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.5 }}
              onDragEnd={(_, info) => { if (info.offset.y > 100) setShowDropdown(false); }}
              className="fixed inset-x-0 bottom-0 sm:fixed sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-14 sm:w-80 sm:rounded-2xl rounded-t-2xl z-[201] sm:pb-0 flex flex-col"
              style={{ 
                height: 'calc(100dvh - env(safe-area-inset-top, 24px) - 24px)',
                background: 'var(--c-surface)',
                border: '1px solid var(--c-border)',
                boxShadow: '0 25px 50px rgba(0,0,0,0.6)',
              }}
            >
              {/* Handle bar for mobile */}
              <div
                className="sm:hidden flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing"
                onPointerDown={(e) => dragControls.start(e)}
                style={{ touchAction: 'none' }}
              >
                <div className="w-10 h-1 rounded-full" style={{ background: 'var(--c-border)' }} />
              </div>
              
              {/* Header */}
              <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--c-border)' }}>
                <h3 className="font-semibold text-lg" style={{ color: 'var(--c-texPri)' }}>{t('notifications.title')}</h3>
                <div className="flex items-center gap-3">
                  {unreadCount > 0 && (
                    <button
                      onClick={dismissAll}
                      className="text-xs px-2 py-1 rounded-full transition-colors"
                      style={{ color: 'var(--c-texSec)', background: 'var(--c-surface-card)' }}
                    >
                      {t('notifications.clearAll', 'Clear all')}
                    </button>
                  )}
                  <button
                    onClick={() => setShowDropdown(false)}
                    className="sm:hidden w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'var(--c-surface-card)' }}
                  >
                    <Icon icon="solar:close-square-linear" width={16} height={16} style={{ color: 'var(--c-texTer)' }} />
                  </button>
                </div>
              </div>

              {/* Notifications List */}
              <div className="overflow-y-auto flex-1">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'var(--c-surface-card)' }}>
                      <Icon icon="solar:bell-linear" width={32} height={32} style={{ color: 'var(--c-texDis)' }} />
                    </div>
                    <p style={{ color: 'var(--c-texTer)' }}>{t('notifications.empty')}</p>
                  </div>
                ) : (
                  <div>
                    {notifications.map((notification, idx) => {
                      const iconStyle = getIconStyle(notification.color);
                      return (
                        <button
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className="w-full p-4 transition-colors flex items-start gap-4"
                          style={{
                            borderBottom: idx < notifications.length - 1 ? '1px solid var(--c-border)' : 'none',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--c-surface-card)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: iconStyle.background }}>
                            <Icon icon={notification.icon} width={24} height={24} style={{ color: iconStyle.color }} />
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <p className="font-medium" style={{ color: 'var(--c-texPri)' }}>{notification.title}</p>
                            <p className="text-sm mt-0.5 truncate" style={{ color: 'var(--c-texTer)' }}>{notification.message}</p>
                          </div>
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => dismissNotification(e, notification.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') dismissNotification(e, notification.id); }}
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer"
                            style={{ background: 'transparent' }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--c-surface-card)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            aria-label={t('notifications.dismiss', 'Dismiss notification')}
                          >
                            <Icon icon="solar:close-square-linear" width={16} height={16} style={{ color: 'var(--c-texTer)' }} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Recent history */}
              {(() => {
                const activeIds = new Set(notifications.map(n => n.id));
                const recent = history.filter(h => !activeIds.has(h.id) && h.type !== 'reminder' && h.type !== 'checkin').slice(0, 3);
                if (recent.length === 0) return null;
                return (
                  <>
                    <div className="px-5 py-2" style={{ borderTop: '1px solid var(--c-border)' }}>
                      <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--c-texTer)' }}>{t('notifications.recent', 'Recent')}</p>
                    </div>
                    {recent.map((item, idx) => {
                      const iconStyle = getIconStyle(item.color);
                      return (
                        <div key={item.id} className="px-5 py-3 flex items-start gap-3 opacity-50" style={{ borderBottom: idx < recent.length - 1 ? '1px solid var(--c-border)' : 'none' }}>
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: iconStyle.background }}>
                            <Icon icon={item.icon || 'solar:bell-linear'} width={20} height={20} style={{ color: iconStyle.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm" style={{ color: 'var(--c-texPri)' }}>{item.title}</p>
                            {item.message && <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--c-texTer)' }}>{item.message}</p>}
                            {item.timestamp && <p className="text-xs mt-0.5" style={{ color: 'var(--c-texDis)' }}>{new Date(item.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              })()}

              {/* See all link */}
              <div className="px-5 py-3 text-center" style={{ borderTop: '1px solid var(--c-border)', paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
                <button
                  onClick={() => { setShowDropdown(false); navigate('/notifications'); }}
                  className="text-sm font-medium"
                  style={{ color: '#818cf8' }}
                >
                  {t('notifications.seeAll', 'See all notifications')}
                </button>
              </div>
            </motion.div>
          </>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
